import db, { Timestamp } from ".";
import {
  ApiSettingAccess,
  ApiSettingPush,
  ApiSettingSms,
} from "../app/models/ApiSetting";
import { converter2 } from "./converter";
import * as lc from "../app/modules/localstorage/index";
import moment from "moment";

// Token cache configuration
const TOKEN_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const TOKEN_CACHE_KEY = "ApiAccess_Token_Cache";

// In-memory cache
let memoryTokenCache: {
  data: ApiSettingAccess | null;
  clientId: string | null;
  expiresAt: number;
} | null = null;

// Interface for cached token data
interface CachedTokenData {
  data: ApiSettingAccess;
  clientId: string;
  cachedAt: number;
  expiresAt: number;
}

// Clear all token caches (memory + localStorage)
export const clearTokenCache = (): void => {
  console.log("🗑️ Clearing all token caches");
  memoryTokenCache = null;
  lc.removeLC(TOKEN_CACHE_KEY);
};

// Get cached token from localStorage
const getCachedTokenFromStorage = (clientId: string): ApiSettingAccess | null => {
  try {
    const cached = lc.getItemLC(TOKEN_CACHE_KEY);
    if (cached && cached.clientId === clientId && cached.expiresAt > Date.now()) {
      console.log("📱 Using cached token from localStorage");
      return cached.data;
    }
  } catch (error) {
    console.error("Error reading cached token from localStorage:", error);
  }
  return null;
};

// Save token to localStorage cache and memory cache
export const saveTokenToCache = (clientId: string, tokenData: ApiSettingAccess): void => {
  try {
    const cacheData: CachedTokenData = {
      data: tokenData,
      clientId,
      cachedAt: Date.now(),
      expiresAt: Date.now() + TOKEN_CACHE_DURATION,
    };
    lc.setItemLC(TOKEN_CACHE_KEY, cacheData);

    // Also update memory cache
    memoryTokenCache = {
      data: tokenData,
      clientId,
      expiresAt: Date.now() + TOKEN_CACHE_DURATION,
    };

    console.log("💾 Token cached to localStorage and memory");
  } catch (error) {
    console.error("Error saving token to cache:", error);
  }
};

export const getAccess = async (): Promise<ApiSettingAccess | undefined> => {
  const clientId = lc.getClientId();
  if (!clientId) {
    console.log("Service API Setting Error : Client ID Not Found");
    return undefined;
  }

  const now = Date.now();

  // 1. Check memory cache first (fastest)
  if (memoryTokenCache && 
      memoryTokenCache.clientId === clientId && 
      memoryTokenCache.expiresAt > now &&
      memoryTokenCache.data) {
    console.log("🚀 Using token from memory cache");
    return memoryTokenCache.data;
  }

  // 2. Check localStorage cache (medium speed)
  const cachedToken = getCachedTokenFromStorage(clientId);
  if (cachedToken) {
    // Update memory cache
    memoryTokenCache = {
      data: cachedToken,
      clientId,
      expiresAt: now + TOKEN_CACHE_DURATION,
    };
    return cachedToken;
  }

  // 3. Fetch from Firestore (slowest, only when necessary)
  console.log("🔄 Fetching fresh token from Firestore (cache miss)");
  try {
    const apiAccess = await db
      .collection("clients")
      .doc(clientId)
      .collection("api")
      .doc("access")
      .withConverter(converter2<ApiSettingAccess>())
      .get();
    
    if (apiAccess.exists) {
      const tokenData = apiAccess.data();
      if (tokenData) {
        // Update both caches
        memoryTokenCache = {
          data: tokenData,
          clientId,
          expiresAt: now + TOKEN_CACHE_DURATION,
        };
        saveTokenToCache(clientId, tokenData);
        console.log("✅ Token fetched and cached for 24 hours");
        return tokenData;
      }
    } else {
      console.log("Service API Setting Error : Client Not Found");
      return undefined;
    }
  } catch (error) {
    console.error("Error fetching token from Firestore:", error);
    return undefined;
  }

  return undefined;
};

export const getPush = async (): Promise<ApiSettingPush | undefined> => {
  const clientId = lc.getClientId();
  if (clientId) {
    try {
      const apiAccess = await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("push")
        .withConverter(converter2<ApiSettingPush>())
        .get();
      if (apiAccess.exists) {
        return apiAccess.data();
      } else {
        console.log("Service API Setting Error : Client Not Found");
        return undefined;
      }
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }
  console.log("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const getSms = async (): Promise<ApiSettingSms | undefined> => {
  const clientId = lc.getClientId();
  if (clientId) {
    try {
      const apiAccess = await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("sms")
        .withConverter(converter2<ApiSettingSms>())
        .get();
      if (apiAccess.exists) {
        return apiAccess.data();
      } else {
        console.log("Service API Setting Error : Client Not Found");
        return undefined;
      }
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }
  console.log("Service API Setting Error : Client ID Not Found");
  return undefined;
};
export const createAccess = async () => {
  const clientId = lc.getClientId();
  const userId = lc.getUserId();
  const timestamp = moment().unix();

  if (clientId && userId) {
    const combinedString = `${clientId}#${userId}#${timestamp}`;
    const secretKey = btoa(combinedString);
    const access = {
      secretKey,
      clientId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    try {
      const apiAccess = await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("access")
        .set(access);
      console.log("Service API Create Access", apiAccess);
      return access;
    } catch (error) {
      console.error("Service API Create Access", error);
      return undefined;
    }
  }
  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const updateAccess = async (accessData: Partial<ApiSettingAccess>) => {
  const clientId = lc.getClientId();
  if (clientId) {
    const access = {
      ...accessData,
      updatedAt: Timestamp.now(),
    };
    try {
      await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("access")
        .set(access);
      return access;
    } catch (error) {
      console.error("Service API Update Access", error);
      return undefined;
    }
  }

  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const createPush = async () => {
  const clientId = lc.getClientId();

  if (clientId) {
    const push = {
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    try {
      const apiPush = await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("push")
        .set(push);
      console.log("Service API Create Push", apiPush);
      return push;
    } catch (error) {
      console.error("Service API Create Push", error);
      return undefined;
    }
  }
  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const updatePush = async (pushData: Partial<ApiSettingAccess>) => {
  const clientId = lc.getClientId();
  if (clientId) {
    const push = {
      ...pushData,
      updatedAt: Timestamp.now(),
    };
    try {
      await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("push")
        .set(push);
      return push;
    } catch (error) {
      console.error("Service API Update Access", error);
      return undefined;
    }
  }

  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const createSms = async () => {
  const clientId = lc.getClientId();

  if (clientId) {
    const sms = {
      failoverSenderMode: "SMS_LONGNUMBER",
      maskingSms: "",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    try {
      const apiSms = await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("sms")
        .set(sms);
      console.log("Service API Create SMS", apiSms);
      return sms;
    } catch (error) {
      console.error("Service API Create SMS", error);
      return undefined;
    }
  }
  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};

export const updateSms = async (smsData: Partial<ApiSettingAccess>) => {
  const clientId = lc.getClientId();
  if (clientId) {
    const sms = {
      ...smsData,
      updatedAt: Timestamp.now(),
    };
    try {
      await db
        .collection("clients")
        .doc(clientId)
        .collection("api")
        .doc("sms")
        .set(sms);
      return sms;
    } catch (error) {
      console.error("Service API Update Access", error);
      return undefined;
    }
  }

  console.error("Service API Setting Error : Client ID Not Found");
  return undefined;
};
